import type { GeoJSONFeature, Map as MapboxMap, MapMouseEvent, Point } from 'mapbox-gl';
import { type RefObject, useEffect, useRef } from 'react';
import { formatNumber } from '@/libs/utils/format';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { LayerId, MAP_BUBBLE_LABEL, MAP_BUBBLE_SURFACE, MAP_BUBBLE_TITLE } from '../constants';
import { positionMapTooltip } from '../utils/positionMapTooltip';

// These layers paint under the field and carry their own hover. The card steps
// aside there and leaves the cursor alone, so their pointer stays put.
const HOVER_YIELDS_TO = [LayerId.TRAFFIC_MAX_AADT, LayerId.TRAFFIC_TILES] as const;

/**
 * Hover card for one heatmap cell: the last quartile of sessions per day and
 * how many HPC stations sit in the cell. Bound to the invisible hit circles,
 * because a heatmap layer does not answer the cursor. `mapId` rebinds the
 * listeners when the map instance is replaced.
 */
export function useCompetitorHeatmapHover(
  map: RefObject<MapboxMap | null>,
  enabled: boolean,
  mapId: string,
) {
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; rebind on enabled/mapId
  useEffect(() => {
    const currentMap = map.current;
    if (!currentMap || !enabled) return;

    function getOrCreateTooltip(hostMap: MapboxMap): HTMLDivElement {
      if (!tooltipRef.current) {
        const tooltip = document.createElement('div');
        tooltip.className = `absolute z-10 pointer-events-none flex w-max max-w-56 flex-col ${MAP_BUBBLE_SURFACE}`;
        tooltip.style.display = 'none';
        hostMap.getContainer().appendChild(tooltip);
        tooltipRef.current = tooltip;
      }
      return tooltipRef.current;
    }

    function hideTooltip() {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    }

    // Built from DOM nodes with textContent, never innerHTML: the figures come
    // from feature properties.
    function renderTooltip(
      hostMap: MapboxMap,
      sessions: number | null,
      competitors: number | null,
      point: Point,
    ) {
      const tooltip = getOrCreateTooltip(hostMap);
      tooltip.replaceChildren();

      if (sessions != null) {
        const value = document.createElement('span');
        value.className = MAP_BUBBLE_TITLE;
        value.textContent = `${formatNumber(sessions, 1)} sessions / day`;
        tooltip.append(value);
      }

      if (competitors != null) {
        const count = document.createElement('span');
        count.className = sessions != null ? `mt-1.5 ${MAP_BUBBLE_LABEL}` : MAP_BUBBLE_TITLE;
        count.textContent = competitorCountLabel(competitors);
        tooltip.append(count);
      }

      tooltip.style.display = 'flex';
      positionMapTooltip({
        tooltip,
        container: hostMap.getContainer(),
        point,
        size: { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
        placement: 'below-right',
      });
    }

    function handleMouseMove(event: MapMouseEvent) {
      const hostMap = map.current;
      if (!hostMap) return;

      if (HOVER_YIELDS_TO.some((layerId) => layerCoversPoint(hostMap, event.point, layerId))) {
        hideTooltip();
        return;
      }

      const features = event.features ?? [];
      const feature = nearestCell(features, event.lngLat) ?? features[0];
      const sessions = readNumber(feature?.properties?.last_quartile_sessions_per_day);
      const competitors = readCount(feature?.properties?.competitor_count);
      if (sessions == null && competitors == null) {
        hostMap.getCanvas().style.cursor = '';
        hideTooltip();
        return;
      }

      hostMap.getCanvas().style.cursor = 'pointer';
      renderTooltip(hostMap, sessions, competitors, event.point);
    }

    function handleMouseLeave() {
      const canvas = map.current?.getCanvas?.();
      if (canvas) canvas.style.cursor = '';
      hideTooltip();
    }

    const cleanupMouseMove = registerMapEvent(
      currentMap,
      'mousemove',
      LayerId.COMPETITORS_HEATMAP_HIT,
      handleMouseMove,
    );
    const cleanupMouseLeave = registerMapEvent(
      currentMap,
      'mouseleave',
      LayerId.COMPETITORS_HEATMAP_HIT,
      handleMouseLeave,
    );

    return () => {
      cleanupMouseMove();
      cleanupMouseLeave();

      tooltipRef.current?.remove();
      tooltipRef.current = null;

      // useMap calls map.remove() first on unmount, and a removed map has no canvas.
      const canvas = currentMap.getCanvas?.();
      if (canvas) canvas.style.cursor = '';
    };
  }, [enabled, mapId]);
}

function layerCoversPoint(hostMap: MapboxMap, point: Point, layerId: string): boolean {
  return Boolean(
    hostMap.getLayer(layerId) && hostMap.queryRenderedFeatures(point, { layers: [layerId] }).length,
  );
}

function competitorCountLabel(count: number): string {
  const formatted = formatNumber(count);
  return count === 1 ? `${formatted} competitor` : `${formatted} competitors`;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readCount(value: unknown): number | null {
  const count = readNumber(value);
  return count == null ? null : Math.round(count);
}

/** The cell whose centroid is closest to the cursor, when several brushes overlap. */
function nearestCell(
  features: GeoJSONFeature[],
  lngLat: { lng: number; lat: number } | undefined,
): GeoJSONFeature | undefined {
  if (!lngLat) return undefined;

  let nearest: GeoJSONFeature | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const feature of features) {
    const point = pointOf(feature);
    if (!point) continue;
    const distance = (point[0] - lngLat.lng) ** 2 + (point[1] - lngLat.lat) ** 2;
    if (distance < nearestDistance) {
      nearest = feature;
      nearestDistance = distance;
    }
  }
  return nearest;
}

function pointOf(feature: GeoJSONFeature): [number, number] | null {
  const geometry = feature.geometry;
  if (!geometry || geometry.type !== 'Point') return null;
  const [lng, lat] = geometry.coordinates;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;
  return [lng, lat];
}
